import React from "react";
import { ScraperJob } from "./types";
import { IconTrash } from "@velaro/velaro-component-lib";
import ScraperJobActivityLog from "./ScraperJobActivityLog";

interface ScraperJobRowProps {
  job: ScraperJob;
  onViewResults: () => void;
  onDelete: () => void;
  onResume: () => void;
  onPublish?: () => void;
  onIndexToKb?: () => void;
  onExportToSharePoint?: () => void;
  onToggleActivity: () => void;
  isActivityOpen: boolean;
  scraperApiUrl: string;
  getHeaders: () => Record<string, string>;
  m365Connected?: boolean;
}

export default function ScraperJobRow({
  job,
  onViewResults,
  onDelete,
  onResume,
  onPublish,
  onIndexToKb,
  onExportToSharePoint,
  onToggleActivity,
  isActivityOpen,
  scraperApiUrl,
  getHeaders,
  m365Connected
}: ScraperJobRowProps) {
  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "bg-yellow-100 text-yellow-800",
      running: "bg-blue-100 text-blue-800",
      completed: "bg-green-100 text-green-800",
      failed: "bg-red-100 text-red-800"
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const progress = parseInt(job.progress) || 0;

  return (
    <>
      <tr className="border-b hover:bg-slate-50">
        <td className="p-3">
          <div className="font-medium">{job.name}</div>
          <div className="text-xs text-slate-400">{job.job_id}</div>
        </td>
        <td className="p-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(job.status)}`}>
            {job.status}
          </span>
        </td>
        <td className="p-3">
          {job.status === "running" || job.status === "pending" ? (
            <div className="w-32">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-2">
                <div
                  className="bg-blue-500 h-2 rounded-full transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <span className="text-sm text-slate-500">
              {job.status === "completed" ? "100%" : "-"}
            </span>
          )}
        </td>
        <td className="p-3">
          <span className="text-sm">
            {job.pages_scraped} pages
          </span>
        </td>
        <td className="p-3">
          <span className="text-sm text-slate-500">
            {formatDate(job.created_at)}
          </span>
        </td>
        <td className="p-3 text-right">
          <div className="flex justify-end gap-2">
            <button
              onClick={onToggleActivity}
              className={`text-sm font-medium ${
                isActivityOpen
                  ? "text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded"
                  : "text-indigo-600 hover:text-indigo-800"
              }`}
            >
              {isActivityOpen ? "Hide Activity" : "Activity"}
            </button>
            {job.status === "completed" && (
              <>
                <button
                  onClick={onViewResults}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Results
                </button>
                {onIndexToKb && (
                  <button
                    onClick={onIndexToKb}
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                    title="Push content into the unified KB Search index for bot Q&A"
                  >
                    Add to KB
                  </button>
                )}
                {onPublish && (
                  <button
                    onClick={onPublish}
                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                  >
                    Publish to Index
                  </button>
                )}
                {m365Connected && onExportToSharePoint && (
                  <button
                    onClick={onExportToSharePoint}
                    className="text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                    title="Export results to SharePoint"
                  >
                    Export to SharePoint
                  </button>
                )}
              </>
            )}
            {job.status === "failed" && job.can_resume && (
              <button
                onClick={onResume}
                className="text-green-600 hover:text-green-800 text-sm font-medium"
              >
                Resume
              </button>
            )}
            {job.status !== "running" && job.status !== "pending" && (
              <button
                onClick={onDelete}
                className="text-red-500 hover:text-red-700"
                title="Delete job"
              >
                <IconTrash className="w-4 h-4" />
              </button>
            )}
          </div>
          {job.error && (
            <div className="text-xs text-red-500 mt-1 max-w-xs truncate" title={job.error}>
              {job.error}
            </div>
          )}
        </td>
      </tr>
      {isActivityOpen && (
        <ScraperJobActivityLog
          jobId={job.job_id}
          jobStatus={job.status}
          scraperApiUrl={scraperApiUrl}
          getHeaders={getHeaders}
        />
      )}
    </>
  );
}
